import { addEndpointErrorLog, extractServerMessage } from "./endpointErrorLogs";

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function shouldRetryStatus(status, retryOnStatuses) {
  return retryOnStatuses.includes(status);
}

export async function fetchWithRetry(
  url,
  options = {},
  retryOptions = {}
) {
  const {
    retries = 2,
    retryDelayMs = 400,
    backoffFactor = 2,
    timeoutMs = 10000,
    retryOnStatuses = [408, 425, 429, 500, 502, 503, 504],
    // Human-readable context shown in the "Ver logs" popup so an error can be
    // traced to what triggered it (e.g. "Login Miami (557418)").
    context = "",
  } = retryOptions;

  const maxAttempts = retries + 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        __skipErrorLog: true,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        return response;
      }

      const canRetry =
        attempt < maxAttempts &&
        shouldRetryStatus(response.status, retryOnStatuses);

      if (!canRetry) {
        const serverMessage = await extractServerMessage(response);
        addEndpointErrorLog({
          source: "fetchWithRetry",
          method: options.method || "GET",
          endpoint: url,
          status: response.status,
          context,
          message: serverMessage || response.statusText || "Request failed",
        });
        return response;
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (attempt >= maxAttempts) {
        addEndpointErrorLog({
          source: "fetchWithRetry",
          method: options.method || "GET",
          endpoint: url,
          status: "NETWORK_ERROR",
          context,
          message: error?.message || "Network request failed",
        });
        throw error;
      }
    }

    const delay = retryDelayMs * backoffFactor ** (attempt - 1);
    await wait(delay);
  }

  throw new Error("Failed to fetch after retries");
}
